'use client';

import Link from 'next/link';
import GuestHeader from '@/components/common/GuestHeader';
import GuestFooter from '@/components/common/GuestFooter';
import Image from 'next/image';
import { useDispatch } from 'react-redux';
import { setIsSignUp, setRequestAction } from '@/features/auth/authSlice';
import { redirect } from 'next/navigation';
import { FREE_TRIAL_DAYS, STARTUP_NUMBER_CONST } from '@/config/consts';

// New AI-focused components
import HeroSection from './HeroSection';
import AICapabilitiesGrid from './AICapabilitiesGrid';
import FinkAIDemoSection from './FinkAIDemoSection';
import FinkInsightsDemoSection from './FinkInsightsDemoSection';
import DocumentAnalysisShowcase from './DocumentAnalysisShowcase';
import { AISparkle } from '@/components/shared/AIComponents';

const GuestHomePage = () => {
  const dispatch = useDispatch();

  const handleSignUp = () => {
    dispatch(setIsSignUp(true));
    dispatch(setRequestAction(null));
    redirect('/auth/login');
  };

  return (
    <div className="bg-white">
      <GuestHeader />

      <main className="relative overflow-hidden">
        {/* New AI-focused Hero Section */}
        <HeroSection />

        {/* AI Capabilities Grid */}
        <AICapabilitiesGrid />

        {/* Fink AI Demo Section - Animated */}
        <FinkAIDemoSection />

        {/* Fink AI Insights Demo - Health Reports */}
        <FinkInsightsDemoSection />

        {/* Document Analysis Showcase */}
        <DocumentAnalysisShowcase />

        {/* Existing Features Section - Enhanced with AI branding */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-200 rounded-full mb-4">
                <AISparkle className="w-4 h-4 text-[#0D60E6]" />
                <span className="text-sm font-semibold text-[#0D52C2]">AI-Powered Features</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Everything You Need to Stay Compliant,
                <br />
                In One Place
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Manage notices, filings, appeals, and deadlines with AI-powered tools
                designed for speed and accuracy.
              </p>
            </div>

            {/* Feature 1: AI-Generated Notice Responses */}
            <div className="w-full flex flex-col lg:flex-row justify-between gap-8 items-center mb-24">
              <div className="w-full lg:w-1/2 max-w-[560px] py-8">
                <div className="flex w-10 h-10 items-center justify-center border border-[#D5D7DA] rounded-[10px] mb-5">
                  <div className="flex w-8 h-8 items-center justify-center border border-[#D5D7DA] rounded-md shadow-lg bg-blue-50">
                    <Image
                      src="/assets/message-chat-circle.svg"
                      alt="Message"
                      width={20}
                      height={20}
                    />
                  </div>
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-[#181D27] mb-4">
                  AI-Generated Notice Responses
                </h3>
                <p className="text-[#535862] text-lg mb-8">
                  Turn complex tax notices into clear, accurate responses within
                  minutes with <span className="font-semibold text-[#0D60E6]">Fink AI</span>.
                </p>
                <div className="space-y-5">
                  {[
                    'Draft compliance, objections, or appeals with AI',
                    'Edit, copy or download, and file faster',
                    'Reduce human error with guided templates',
                  ].map((text, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-7 h-7 flex items-center justify-center flex-shrink-0">
                        <Image src="/assets/check-icon.svg" alt="Check" width={28} height={28} />
                      </div>
                      <p className="text-[#535862] text-lg font-normal">{text}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="w-full lg:w-1/2">
                <Image
                  src="/assets/notice-content.svg"
                  alt="Notice Content"
                  width={762}
                  height={512}
                  className="w-full h-auto"
                />
              </div>
            </div>

            {/* Feature 2: Smart Compliance Calendar */}
            <div className="w-full flex flex-col-reverse lg:flex-row justify-start gap-8 items-center mb-24">
              <div className="w-full lg:w-1/2">
                <Image
                  src="/assets/smart-content.svg"
                  alt="Smart Content"
                  width={762}
                  height={512}
                  className="w-full h-auto"
                />
              </div>
              <div className="w-full lg:w-1/2 max-w-[560px] py-8">
                <div className="flex w-10 h-10 items-center justify-center border border-[#D5D7DA] rounded-[10px] mb-5">
                  <div className="flex w-8 h-8 items-center justify-center border border-[#D5D7DA] rounded-md shadow-lg bg-blue-50">
                    <Image src="/assets/zap-fast.svg" alt="Zap Fast" width={20} height={20} />
                  </div>
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-[#181D27] mb-4">
                  Smart Compliance Calendar
                </h3>
                <p className="text-[#535862] text-lg mb-8">
                  Stay ahead of deadlines with AI-powered automated reminders.
                </p>
                <div className="space-y-5">
                  {[
                    'Track advance tax, TDS, GST, and ITR dates',
                    'Get alerts for upcoming filings and proceedings',
                    'Sync with your workflow, never miss a due date',
                  ].map((text, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-7 h-7 flex items-center justify-center flex-shrink-0">
                        <Image src="/assets/check-icon.svg" alt="Check" width={28} height={28} />
                      </div>
                      <p className="text-[#535862] text-lg font-normal">{text}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Feature 3: Automated Demand Tracking */}
            <div className="w-full flex flex-col lg:flex-row justify-between gap-8 items-center">
              <div className="w-full lg:w-1/2 max-w-[560px] py-8">
                <div className="flex w-10 h-10 items-center justify-center border border-[#D5D7DA] rounded-[10px] mb-5">
                  <div className="flex w-8 h-8 items-center justify-center border border-[#D5D7DA] rounded-md shadow-lg bg-blue-50">
                    <Image
                      src="/assets/chart-breakout-square.svg"
                      alt="Chart Breakout Square"
                      width={20}
                      height={20}
                    />
                  </div>
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-[#181D27] mb-4">
                  Automated Demand Tracking
                </h3>
                <p className="text-[#535862] text-lg mb-8">
                  Simplify demands with AI-structured monitoring.
                </p>
                <div className="space-y-5">
                  {[
                    'Real-time view of completed vs. pending demands of all clients',
                    'Drill down into clients and sections',
                    'Export reports for team or regulator submission',
                  ].map((text, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-7 h-7 flex items-center justify-center flex-shrink-0">
                        <Image src="/assets/check-icon.svg" alt="Check" width={28} height={28} />
                      </div>
                      <p className="text-[#535862] text-lg font-normal">{text}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="w-full lg:w-1/2">
                <Image
                  src="/assets/demand-content.svg"
                  alt="Demand Content"
                  width={762}
                  height={512}
                  className="w-full h-auto"
                />
              </div>
            </div>
          </div>
        </section>

        <div className="h-[1px] bg-[#E9EAEB] mx-4 sm:mx-[112px]"></div>

        {/* Social Proof Section - Enhanced */}
        <section className="py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col lg:flex-row justify-between gap-16 items-center">
              <div className="w-full lg:w-1/2 max-w-[560px]">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-200 rounded-full mb-6">
                  <span className="w-2 h-2 rounded-full bg-[#0D60E6]"></span>
                  <span className="text-sm font-medium text-[#0D52C2]">Powered by AI</span>
                </div>
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-[#181D27] mb-8">
                  Join {STARTUP_NUMBER_CONST.toLocaleString()}+ CAs
                  <br />
                  powered by <span className="text-[#0D60E6] font-bold">Finkr AI</span>
                </h2>
                <div className="space-y-5">
                  {[
                    `${FREE_TRIAL_DAYS}-day free trial`,
                    'Personalized onboarding',
                    'Access to all AI features',
                  ].map((text, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-7 h-7 flex items-center justify-center flex-shrink-0">
                        <Image src="/assets/check-icon.svg" alt="Check" width={28} height={28} />
                      </div>
                      <p className="text-[#535862] text-lg font-normal">{text}</p>
                    </div>
                  ))}
                </div>
                <Link
                  href="/plans"
                  className="h-12 mt-12 px-[18px] shadow-lg shadow-blue-500/20 text-base font-bold rounded-lg transition-all duration-300 inline-flex items-center justify-center bg-gradient-to-r from-[#0D60E6] to-[#0D52C2] text-white hover:shadow-xl hover:shadow-blue-500/30 hover:scale-105"
                >
                  Get started
                </Link>
              </div>
              <div className="w-full lg:w-1/2">
                <Image
                  src="/assets/cta-content.svg"
                  alt="CTA Content"
                  width={762}
                  height={512}
                  className="w-full h-auto"
                />
              </div>
            </div>
          </div>
        </section>

        <GuestFooter />
      </main>
    </div>
  );
};

export default GuestHomePage;
